import React, { useContext } from "react";
import STBoardCard from "../../assets/styles/molecules/boardCard.module.scss";
import { useSwiperSettings } from "../../hooks/useSwiperSettings";
import "swiper/modules/navigation/navigation.scss"; // Navigation module
import "swiper/modules/pagination/pagination.scss"; // Pagination module
import { Swiper, SwiperSlide } from "swiper/react/swiper-react.js";
import "swiper/swiper.scss"; // core Swiper
import { Navigation, Pagination } from "swiper";
import moment from "moment";
import Account from "../atoms/icons/AccountIcon";
import BoardActions from "../organisms/my_page/message_board/BoardActions";
import CommentList from "../organisms/my_page/message_board/CommentList";
import { SiteContext } from "../../providers/SiteProvider";
import MessageBoardNickname from "../atoms/MessageBoardNickname";

const BOARD_PENDING_STATUS = 3;
export type BoardItem = {
  id: number;
  avatarUrl: string;
  nickname: string;
  title: string;
  content: string;
  createdAt: string;
  imageUrls: string[];
  commentCount: number;
  commentFlag?: number;
  likeFlag: number;
  likeCount: number;
  categoryId: number;
  status?: number;
  isPublic?: boolean;
  uid?: string;
};

type Props = {
  boardDetail: BoardItem;
  isMyBoard?: boolean;
  isMyActivityBoard?: boolean;
  onDeleteSuccess?: () => void;
  className?: string;
  boardCardClassName?: string;
};

const BoardCard: React.FC<Props> = ({
  boardDetail,
  isMyBoard = false,
  isMyActivityBoard = false,
  onDeleteSuccess = () => {},
  className = "",
}) => {
  const {
    avatarUrl,
    nickname,
    createdAt,
    title,
    content,
    imageUrls = [],
    commentCount = 0,
    commentFlag = 0,
    likeFlag,
    likeCount = 0,
    categoryId,
    id: boardId,
    status,
    isPublic,
  } = boardDetail;
  const mainColor = useContext(SiteContext).mainColor;
  const getSlideImage = (imageUrls: string[]) => {
    if (imageUrls.length === 3) {
      return 2.2;
    }
    return imageUrls.length;
  };
  const imageUrlsLength = imageUrls.length;
  const breakpoints = {
    default: {
      slidesPerView: getSlideImage(imageUrls),
      slidesPerGroup: getSlideImage(imageUrls),
      centeredSlides: false,
      loopThreshold: getSlideImage(imageUrls),
    },
    sp: {
      slidesPerView: getSlideImage(imageUrls),
      slidesPerGroup: 1,
      centeredSlides: false,
      loopThreshold: 2,
    },
  };
  const { shouldLoop, slidesPerView, slidesPerGroup, centeredSlides } =
    useSwiperSettings(imageUrlsLength, breakpoints);

  const generateStatus = () => {
    let text = "";
    let color = "";
    if (isPublic) {
      text = "公開中";
      color = "#222222";
    } else if (!isPublic && status === BOARD_PENDING_STATUS) {
      text = "承認待ち";
      color = "#EF2628";
    } else if (!isPublic) {
      text = "非公開";
      color = "#222222";
    }
    if (text && color) {
      return (
        <div className={STBoardCard.status} style={{ color: color }}>
          {text}
        </div>
      );
    }
  };

  return (
    <div
      className={`${STBoardCard.boardCardBox} ${
        isMyBoard || isMyActivityBoard ? STBoardCard.boardCardBoxMyPage : ""
      } ${className ? STBoardCard[className] : ""}`}
    >
      <div
        className={`${STBoardCard.boardCard} ${
          isMyBoard || isMyActivityBoard ? STBoardCard.boardCardMyPage : ""
        }`}
        onClick={() => {
          window.location.href = `/message-board/categories/${categoryId}/${boardId}`;
        }}
      >
        {isMyBoard && (
          <BoardActions
            targetId={boardId}
            isBoard
            onEdit={() => {
              window.location.href = `/message-board/categories/${categoryId}/${boardId}/edit`;
            }}
            onDelete={onDeleteSuccess}
          />
        )}
        <div className={STBoardCard.header}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="Avatar" className={STBoardCard.avatar} />
          ) : (
            <div className={STBoardCard.avatar}>
              <Account width={30} height={30} />
            </div>
          )}
          <div className={STBoardCard.info}>
            <MessageBoardNickname
              component="p"
              nickname={nickname}
              className={STBoardCard.nickname}
            />
            <p className={STBoardCard.createdAt}>
              {moment(createdAt).format("YYYY/MM/DD")}
            </p>
          </div>
          {isMyBoard && generateStatus()}
        </div>
        <h3 className={STBoardCard.title}>{title}</h3>
        {imageUrlsLength === 0 && (
          <p className={STBoardCard.contentMaxHeight}>{content.trim()}</p>
        )}

        {imageUrlsLength !== 0 && (
          <p className={STBoardCard.content}>{content.trim()}</p>
        )}

        {imageUrlsLength !== 0 && (
          <div
            className={STBoardCard.images}
            style={{
              width:
                imageUrlsLength === 1
                  ? "calc(100% * 5 / 11)"
                  : imageUrlsLength === 2
                  ? "calc(100% * 10 / 11)"
                  : "100%",
            }}
          >
            <Swiper
              modules={[Navigation, Pagination]}
              loop={false}
              spaceBetween={0}
              slidesPerView={slidesPerView}
              slidesPerGroup={slidesPerGroup}
              centeredSlides={centeredSlides}
              loopFillGroupWithBlank={false}
            >
              {imageUrls.map((image: string, index: number) => (
                <SwiperSlide key={`slide-${index}`}>
                  <div
                    style={{
                      paddingRight: "5px",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <img
                      key={index}
                      src={image}
                      alt={`img-${index}`}
                      className={`${STBoardCard.imageItem} forum-board-image`}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        <div
          className={STBoardCard.footer}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "4px",
          }}
        >
          {/* Comments */}
          <div className={STBoardCard.comments}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="10"
              viewBox="0 0 12 10"
              fill="none"
            >
              <path
                d="M1.33317 1H10.6665V7H2.01567L1.33317 7.585V1ZM1.33317 0C0.691504 0 0.172337 0.45 0.172337 1L0.166504 10L2.49984 8H10.6665C11.3082 8 11.8332 7.55 11.8332 7V1C11.8332 0.45 11.3082 0 10.6665 0H1.33317ZM2.49984 5H7.1665V6H2.49984V5ZM2.49984 3.5H9.49984V4.5H2.49984V3.5ZM2.49984 2H9.49984V3H2.49984V2Z"
                fill={!!commentFlag && !!commentCount ? mainColor : "#999999"}
              />
            </svg>
            <span>{Number(commentCount)}</span>
          </div>

          {/* Likes */}
          <div className={STBoardCard.likes}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="17"
              height="15"
              viewBox="0 0 16 11"
              fill={likeFlag > 0 ? mainColor : "none"}
            >
              <path
                d="M7.00018 2.35286C6.82239 2.35288 6.65797 2.25849 6.56834 2.10496C6.00993 1.14849 4.95877 0.5 3.74722 0.5C1.94393 0.5 0.5 1.92896 0.5 3.66984C0.5 4.25373 0.645504 4.99454 1.08726 5.65087C2.91533 8.36604 5.97738 10.7531 7.02369 11.4925C7.03743 11.5027 7.05708 11.5027 7.07081 11.4925C8.02285 10.7531 11.0846 8.36603 12.913 5.65087C13.3548 4.99453 13.5003 4.25397 13.5003 3.66984C13.5003 1.92896 12.0563 0.5 10.253 0.5C9.04144 0.5 7.99008 1.14853 7.43198 2.10488C7.34237 2.25843 7.17796 2.35285 7.00018 2.35286Z"
                stroke={likeFlag > 0 ? mainColor : "#999999"}
                strokeLinejoin="round"
              ></path>
            </svg>
            <span>{Number(likeCount)}</span>
          </div>
        </div>

        {isMyActivityBoard && (
          <CommentList boardId={boardId} categoryId={categoryId} />
        )}
      </div>
    </div>
  );
};

export default BoardCard;
